import { TextBodyExtraSmall2, TextBodyLarge, TextBodyMedium, TextHeadlineLarge, TextLabelLarge2 } from '@/components/text/Texts';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { MdAdd, MdClose, MdOutlineDelete, MdOutlineModeEdit } from 'react-icons/md';
import { Column, Row } from '@/components/container/Container';
import { cn } from '@/components/ui/cn';
import { createTypedModal, showTypedModal, useTypedModal } from '@/common/common_utils';
import AddBillingInfoFormModal from './AddBillingInfoFormModal';
import { BillingApi } from '../billing.api';
import { showNotification } from '@/modules/notification/engine/notification.library';

const AddBillingInfoModal = createTypedModal<{}, undefined>(() => {
	const modal = useTypedModal();

	const billingQ = useQuery({ queryKey: ['AddBillingInfoModal.billingInfo'], queryFn: BillingApi.getBillingInfo });
	const billingInfo = billingQ.data;
	const hasBillingInfo = !!billingInfo?.LegalName;


	const handleDeleteClick = () => {
		// We have the delete icon in figma. Though the deletion doesn't make sense. So, we leave the icon but behave smart.
		showNotification('Deletion of billing information is not allowed. Please contact support if you need to remove your billing information.');
	};

	return (
		<Dialog
			visible={true}
			onHide={() => modal.resolveRemove()}
			draggable={false}
			maximized
			closable
			closeIcon={(
				<div className="p-2 bg-secondaryContainer duration-200 hover:bg-gray-200">
					<MdClose size={24} className="text-secondary" />
				</div>
			)}
			pt={{
				root: { className: 'bg-primary100' },
				headerIcons: { className: 'h-full' },
				headerTitle: { className: 'absolute' },
				header: { className: 'bg-transparent h-[92px] px-8 mb-5' },
				content: { className: 'flex flex-col grow-0 w-[732px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)] mx-auto px-8 py-[52px] rounded-[28px] bg-white' }
			}}
		>
			<TextHeadlineLarge className="mb-2 text-neutral17" text="Add Billing Information" />
			<TextBodyMedium className="mb-9 text-neutral17">
				By providing relevant banking information, this will facilitate
				smooth and secure transactions. Completing this step ensures
				seamless billing processes and enables efficient transactions
				within the Xpress for Business platform, enhancing operational
				effectiveness and financial management.
			</TextBodyMedium>


			{hasBillingInfo && (
				/* TODO[Phase2]: when editing button title on the dialog which gets opened should not be "Add..."
					 Not sure if we need Edit/Delete functionality in onboarding at all. */
				<Row className="gap-4 h-20 mb-9 px-6 bg-primary100 rounded-[12px] items-center">
					<Column className="gap-2 grow">
						<TextBodyLarge className="text-onSurface" text="Xpress" />
						<TextBodyExtraSmall2
							className="text-primary"
							text={`${billingInfo?.LegalName}, ${billingInfo?.Address1}, ${billingInfo?.Province?.Name}, ${billingInfo?.City?.Name}, ${billingInfo?.PostalCode}`}
						/>
					</Column>
					<Row className="gap-2">
						<Button
							className="h-10 w-10 m-1 rounded-[100px] bg-secondaryContainer"
							icon={<MdOutlineModeEdit size={24} className="text-neutral17" />}
							onClick={async () => await showTypedModal(AddBillingInfoFormModal, {
								onInfoAdded: () => {
									modal.resolveRemove()
									//billingQ.refetch();
								}
							})}
						/>
						<Button
							className="h-10 w-10 m-1 rounded-[100px] bg-secondaryContainer"
							icon={<MdOutlineDelete size={24} className="text-neutral17" />}
							onClick={handleDeleteClick}
						/>
					</Row>
				</Row>
			)}

			<Button
				disabled={hasBillingInfo}
				onClick={async () => await showTypedModal(AddBillingInfoFormModal, {
					onInfoAdded: () => {
						modal.resolveRemove()
					}
				})}
				className={cn(
					'self-start h-12 gap-2 pl-4 pr-6 rounded-[100px] cursor-pointer duration-300',
					!hasBillingInfo ? 'bg-primary' : 'bg-neutral80 pointer-events-none'
				)}
			>
				<MdAdd size={18} className={!hasBillingInfo ? 'text-white' : 'text-[#949494]'} />
				<TextLabelLarge2 className={!hasBillingInfo ? 'text-white' : 'text-[#949494]'} text="Add billing information" />
			</Button>
		</Dialog>
	);
});

export default AddBillingInfoModal;
